import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { CS } from "@/constants/testIds/clickshield";

const ACTION_STYLE = {
  Blocked: "text-red-400 bg-red-500/10 border-red-500/20",
  Flagged: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  Allowed: "text-[#39FF14] bg-[#39FF14]/10 border-[#39FF14]/20",
};

const scoreColor = (s) => (s >= 70 ? "text-red-400" : s >= 45 ? "text-amber-400" : "text-[#39FF14]");

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  return `${Math.floor(s / 60)}m ago`;
};

export const FraudLog = () => {
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/dashboard/fraud-log");
      setRows(data.rows);
      setSource(data.data_source || "demo");
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl text-white">Live Fraud Log</h2>
          <p className="text-sm text-slate-400 mt-1 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${source === "live" ? "bg-[#39FF14]" : source === "demo" ? "bg-amber-400" : "bg-slate-500"} animate-pulse-glow`} />
            <span data-testid="fraud-data-source">
              {source === null
                ? "Connecting to click stream…"
                : source === "live"
                ? "Live data · your tracked clicks · auto-refreshes"
                : "Demo data · install your snippet for live clicks"}
            </span>
          </p>
        </div>
        <button
          data-testid={CS.fraudRefresh}
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm text-white hover:border-cyan-neon/50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {!rows ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl bg-[#0B101A] border border-white/8 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="border-b border-white/8 bg-white/[0.02]">
                  {["IP Address", "Location", "ISP", "Device", "Fraud Score", "Action", "Time"].map((h) => (
                    <th key={h} className="text-left px-5 py-3.5 font-mono text-[10px] tracking-widest uppercase text-slate-500 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <motion.tr
                    key={`${r.ip}-${i}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(i * 0.015, 0.4) }}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-5 py-3 font-mono text-sm text-white">{r.ip}</td>
                    <td className="px-5 py-3 text-sm text-slate-300">{r.country}</td>
                    <td className="px-5 py-3 text-sm text-slate-400">{r.isp}</td>
                    <td className="px-5 py-3 text-sm text-slate-400">{r.device}</td>
                    <td className="px-5 py-3">
                      <span className={`font-mono text-sm font-semibold ${scoreColor(r.fraud_score)}`}>{r.fraud_score}</span>
                      <span className="text-slate-600 text-xs">/100</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-md border font-mono text-[10px] tracking-wider uppercase ${ACTION_STYLE[r.action]}`}>
                        {r.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{timeAgo(r.timestamp)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
